/**
 * Admin — Classifier Shadow Report
 * ─────────────────────────────────
 * v2.13.0 — Operator-facing dashboard for the shadow audio classifier
 * (inaSpeechSegmenter). Visualises the cross-table classification vs
 * Shazam result and surfaces the two headline safety metrics:
 *   • Potential Fingerprint Reduction %  — savings if we activate gating
 *   • Potential Lost Recognitions %      — songs we'd wrongly skip
 *
 * Hits /api/admin/classifier-report?hours=N (admin auth).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  Radio, Mic, Music2, Volume2, ShieldAlert, Download,
} from 'lucide-react';
import { getBackendUrl } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';

const API = getBackendUrl();

const CLASS_COLOURS = {
  music:           '#16a34a',
  speech:          '#fbbf24',
  'speech+music':  '#60a5fa',
  noise:           '#94a3b8',
  unavailable:     '#475569',
  timeout:         '#dc2626',
  error:           '#dc2626',
};

const CLASS_ICONS = {
  music:           Music2,
  speech:          Mic,
  'speech+music':  Volume2,
  noise:           Volume2,
};

const fmtPct = (n) => n == null ? '—' : `${Number(n).toFixed(2)} %`;
const fmtNum = (n) => n == null ? '—' : Number(n).toLocaleString('el-GR');

// ── Headline metric card ────────────────────────────────────────────────────
const MetricCard = ({ title, value, suffix, hint, hex, testId }) => (
  <div
    data-testid={testId}
    className="bg-gradient-to-br from-slate-800/70 to-slate-900/80 border border-white/10 rounded-xl p-5"
  >
    <div className="text-[11px] uppercase tracking-wider text-white/55 mb-2">
      {title}
    </div>
    <div className="flex items-baseline gap-1">
      <span
        className="text-4xl font-bold tabular-nums"
        style={{ color: hex || '#ffffff' }}
      >
        {value}
      </span>
      {suffix && (
        <span className="text-base text-white/55">{suffix}</span>
      )}
    </div>
    {hint && (
      <div className="text-[11px] text-white/45 mt-2 leading-snug">{hint}</div>
    )}
  </div>
);

// ── Main page ──────────────────────────────────────────────────────────────
const ClassifierReport = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [hours, setHours] = useState(48);
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token — please log in as admin');

      const [reportResp, healthResp] = await Promise.all([
        fetch(`${API}/api/admin/classifier-report?hours=${hours}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/admin/classifier-report/health`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!reportResp.ok) {
        throw new Error(`Report ${reportResp.status}: ${await reportResp.text()}`);
      }
      if (!healthResp.ok) {
        throw new Error(`Health ${healthResp.status}: ${await healthResp.text()}`);
      }
      setData(await reportResp.json());
      setHealth(await healthResp.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // v2.13.5 — CSV export. Downloads the current report (with the
  // selected hours window) as a CSV file. Uses the auth token to hit
  // the protected endpoint, then triggers a browser download via a
  // Blob URL. Works on production through REACT_APP_BACKEND_URL.
  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token — please log in as admin');
      const resp = await fetch(
        `${API}/api/admin/classifier-report.csv?hours=${hours}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`CSV ${resp.status}: ${await resp.text()}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.download = `classifier_report_${hours}h_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // v2.13.18 — JSON export. Downloads the raw JSON response of the
  // current report. Operator-friendly: same window as CSV, same auth.
  // Useful for offline analysis or sharing with support.
  const downloadJson = () => {
    if (!data) return;
    try {
      const blob = new Blob(
        [JSON.stringify(data, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.download = `classifier_report_${hours}h_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 lg:p-8" data-testid="classifier-report-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <button
              data-testid="back-to-admin-btn"
              onClick={() => navigate('/admin')}
              className="p-2 rounded-md hover:bg-white/10 transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Radio className="w-6 h-6 text-amber-400" />
                {/* v2.13.18 — strip the hard-coded "(Shadow)" suffix
                    from the i18n title and append the live gate_mode
                    so the operator immediately sees whether gating is
                    actually suppressing Shazam calls. */}
                {(t('clfReport_title') || '').replace(/\s*\([^)]+\)\s*$/, '')}
                {health?.gate_mode && (
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                    health.gate_mode === 'active'
                      ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                      : health.gate_mode === 'shadow'
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                      : 'bg-slate-500/20 text-slate-300 border border-slate-500/40'
                  }`} data-testid="title-mode-suffix">
                    {health.gate_mode === 'active' ? 'LIVE GATING' : health.gate_mode.toUpperCase()}
                  </span>
                )}
              </h1>
              <div className="text-sm text-white/55">
                {t('clfReport_subtitle')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="hours-selector"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value, 10))}
              className="bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-sm"
            >
              <option value={1}>1h</option>
              <option value={6}>6h</option>
              <option value={24}>24h</option>
              <option value={48}>48h</option>
              <option value={168}>7d</option>
            </select>
            <button
              data-testid="refresh-btn"
              onClick={fetchReport}
              disabled={loading}
              className="px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-semibold text-sm flex items-center gap-1"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t('clfReport_refresh')}
            </button>
            <button
              data-testid="download-csv-btn"
              onClick={downloadCsv}
              disabled={loading || !data}
              title={t('clfReport_downloadCsv') || 'Download CSV'}
              className="px-3 py-2 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-1 border border-white/10"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
            <button
              data-testid="download-json-btn"
              onClick={downloadJson}
              disabled={loading || !data}
              title={t('clfReport_downloadJson') || 'Download JSON'}
              className="px-3 py-2 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-1 border border-white/10"
            >
              <Download className="w-4 h-4" />
              JSON
            </button>
          </div>
        </div>

        {/* Gate mode badge */}
        {health && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm" data-testid="gate-mode-banner">
            <span className="text-white/50">{t('clfReport_gateMode')}:</span>
            <span
              className={`px-2 py-1 rounded font-mono text-xs font-bold ${
                health.gate_mode === 'shadow' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                : health.gate_mode === 'active' ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                : 'bg-slate-500/20 text-slate-300 border border-slate-500/40'
              }`}
            >
              {(health.gate_mode || 'unknown').toUpperCase()}
            </span>
            <span className="text-white/40 ml-2">
              {health.classifier_enabled
                ? <span className="text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {t('clfReport_classifierReady')}
                  </span>
                : <span className="text-red-400 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t('clfReport_classifierUnavail')}
                    {health.classifier_unavailable_reason && (
                      <span className="text-xs text-white/40 ml-1">
                        ({health.classifier_unavailable_reason})
                      </span>
                    )}
                  </span>}
            </span>
          </div>
        )}

        {error && (
          <div data-testid="error-banner" className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg p-4 mb-4">
            <div className="font-bold">Error</div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        )}

        {data && (
          <>
            {/* No data hint */}
            {data.total_samples === 0 && (
              <div data-testid="no-samples-banner" className="bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg p-5 mb-6 text-sm leading-relaxed">
                <div className="font-bold mb-1 flex items-center gap-1">
                  <ShieldAlert className="w-4 h-4" /> {t('clfReport_noSamplesTitle')}
                </div>
                {t('clfReport_noSamplesHint')}
              </div>
            )}

            {/* Headline metrics: when active gating is ON, we surface
                the ACTUAL reduction (from by_shazam.SKIPPED) instead
                of the potential one — and add a dedicated calls-saved
                card. In shadow/off mode, we keep showing the
                potential (what-if) numbers as before. */}
            {(() => {
              const isActive = health?.gate_mode === 'active';
              const skippedCount = data.by_shazam?.SKIPPED || 0;
              const totalRealCalls =
                (data.by_shazam?.HIT || 0)
                + (data.by_shazam?.NOMATCH || 0)
                + (data.by_shazam?.ERROR || 0)
                + skippedCount;
              const actualReductionPct = totalRealCalls > 0
                ? (skippedCount / totalRealCalls) * 100
                : 0;
              return (
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
                  <MetricCard
                    testId="metric-music-pct"
                    title={t('clfReport_musicPct')}
                    value={fmtPct(data.by_class_pct?.music)}
                    hex={CLASS_COLOURS.music}
                  />
                  <MetricCard
                    testId="metric-speech-pct"
                    title={t('clfReport_speechPct')}
                    value={fmtPct(data.by_class_pct?.speech)}
                    hex={CLASS_COLOURS.speech}
                  />
                  <MetricCard
                    testId="metric-speech-music-pct"
                    title={t('clfReport_speechMusicPct')}
                    value={fmtPct(data.by_class_pct?.['speech+music'])}
                    hex={CLASS_COLOURS['speech+music']}
                  />
                  {isActive ? (
                    <MetricCard
                      testId="metric-calls-saved"
                      title={t('clfReport_callsSaved') || 'Shazam Calls Saved'}
                      value={fmtNum(skippedCount)}
                      hex="#fbbf24"
                      hint={t('clfReport_callsSaved_hint')}
                    />
                  ) : (
                    <MetricCard
                      testId="metric-noise-pct"
                      title={t('clfReport_noisePct')}
                      value={fmtPct(data.by_class_pct?.noise)}
                      hex={CLASS_COLOURS.noise}
                    />
                  )}
                  <MetricCard
                    testId="metric-reduction-pct"
                    title={
                      isActive
                        ? (t('clfReport_actualReduction') || 'Actual Fingerprint Reduction')
                        : t('clfReport_potentialReduction')
                    }
                    value={fmtPct(isActive ? actualReductionPct : data.potential_reduction_pct)}
                    hex="#4ade80"
                    hint={
                      isActive
                        ? (t('clfReport_actualReduction_hint') || '')
                        : t('clfReport_potentialReduction_hint')
                    }
                  />
                  <MetricCard
                    testId="metric-lost-hits-pct"
                    title={t('clfReport_potentialLost')}
                    value={fmtPct(data.potential_lost_hits_pct)}
                    hex={
                      (data.potential_lost_hits_pct || 0) < 5 ? '#4ade80'
                        : (data.potential_lost_hits_pct || 0) < 15 ? '#fbbf24'
                        : '#fca5a5'
                    }
                    hint={t('clfReport_potentialLost_hint')}
                  />
                </div>
              );
            })()}

            {/* Cross-table */}
            <div className="bg-slate-900/40 border border-white/10 rounded-xl p-4 mb-6" data-testid="cross-table-card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-white/70">
                  {t('clfReport_crossTableTitle')}
                </h2>
                <div className="text-xs text-white/45">
                  {t('clfReport_totalSamples')}: <span className="text-white font-bold">{fmtNum(data.total_samples)}</span>
                  &nbsp;·&nbsp;
                  {t('clfReport_evaluable')}: <span className="text-white font-bold">{fmtNum(data.evaluable_samples)}</span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] text-white/45 uppercase">
                    <th className="text-left p-2">{t('clfReport_classification')}</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-right p-2 text-emerald-400">HIT</th>
                    <th className="text-right p-2 text-red-400">NOMATCH</th>
                    <th className="text-right p-2 text-amber-400" title="Calls blocked by active gating">SKIPPED</th>
                    <th className="text-right p-2">{t('clfReport_hitRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cross_table?.map((row) => {
                    const Icon = CLASS_ICONS[row.classification] || Volume2;
                    return (
                      <tr
                        key={row.classification}
                        className="border-t border-white/5"
                        data-testid={`cross-row-${row.classification}`}
                      >
                        <td className="p-2">
                          <span className="inline-flex items-center gap-2">
                            <Icon className="w-3.5 h-3.5" style={{ color: CLASS_COLOURS[row.classification] }} />
                            <span style={{ color: CLASS_COLOURS[row.classification] }}>
                              {row.classification}
                            </span>
                          </span>
                        </td>
                        <td className="text-right tabular-nums p-2">{fmtNum(row.total)}</td>
                        <td className="text-right tabular-nums p-2 text-emerald-300">{fmtNum(row.HIT)}</td>
                        <td className="text-right tabular-nums p-2 text-red-300">{fmtNum(row.NOMATCH)}</td>
                        <td className="text-right tabular-nums p-2 text-amber-300" data-testid={`cross-row-${row.classification}-skipped`}>{fmtNum(row.SKIPPED || 0)}</td>
                        <td className="text-right tabular-nums p-2">{fmtPct(row.hit_rate_pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[11px] text-white/40 mt-3 leading-relaxed">
                {t('clfReport_crossTableHint')}
              </div>
            </div>

            {/* Per-station breakdown */}
            <div className="bg-slate-900/40 border border-white/10 rounded-xl p-4" data-testid="per-station-card">
              <h2 className="text-sm font-bold uppercase tracking-wider text-white/70 mb-3">
                {t('clfReport_perStationTitle')}
              </h2>
              {(!data.per_station || data.per_station.length === 0) ? (
                <div className="text-sm text-white/50 italic py-3 text-center">
                  {t('clfReport_noStationData')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-white/45 uppercase">
                        <th className="text-left p-2">{t('clfReport_station')}</th>
                        <th className="text-right p-2">Samples</th>
                        <th className="text-right p-2 text-emerald-300">Music %</th>
                        <th className="text-right p-2 text-amber-300">Speech %</th>
                        <th className="text-right p-2 text-blue-300">Spch+Mus %</th>
                        <th className="text-right p-2 text-slate-300">Noise %</th>
                        <th className="text-right p-2">Would FP</th>
                        <th className="text-right p-2">Would Skip</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.per_station.map((s, i) => (
                        <tr key={i} className="border-t border-white/5" data-testid={`station-row-${s.station_id}`}>
                          <td className="p-2 text-white/85 truncate max-w-[200px]">
                            {s.station_name || s.station_id}
                          </td>
                          <td className="text-right tabular-nums p-2">{fmtNum(s.total)}</td>
                          <td className="text-right tabular-nums p-2 text-emerald-200">{fmtPct(s.music_pct)}</td>
                          <td className="text-right tabular-nums p-2 text-amber-200">{fmtPct(s.speech_pct)}</td>
                          <td className="text-right tabular-nums p-2 text-blue-200">{fmtPct(s.speech_music_pct)}</td>
                          <td className="text-right tabular-nums p-2 text-slate-300">{fmtPct(s.noise_pct)}</td>
                          <td className="text-right tabular-nums p-2 text-emerald-300">{fmtNum(s.would_fingerprint)}</td>
                          <td className="text-right tabular-nums p-2 text-red-300">{fmtNum(s.would_skip)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-xs text-white/40 mt-6 leading-relaxed">
              {t('clfReport_window')}: <span className="font-mono">{data.from_ts} → {data.to_ts}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClassifierReport;
